import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { axiosInstance } from '../../utils/axiosInstance';
import {
  CycleCatalogItem,
  DRYER_MINUTE_OPTIONS,
  GetPriceResult,
  MachineKind,
  axiosErrorMessage,
  cycleLabel,
  defaultTemperatureId,
  filterCycleCatalog,
} from './machineControlTypes';
import './machineControlModal.scss';

export type MachineStartModalTarget = {
  macId: string;
  name: string;
  kind: MachineKind;
};

type Props = {
  open: boolean;
  target: MachineStartModalTarget | null;
  busy?: boolean;
  error?: string | null;
  requestedBy: string;
  onClose: () => void;
  onSubmit: (payload: {
    kind: MachineKind;
    cycleId?: string;
    temperatureId?: string | null;
    useCurrentCycle?: boolean;
    minutes?: number;
  }) => void;
};

const MachineStartModal = ({
  open,
  target,
  busy,
  error,
  requestedBy,
  onClose,
  onSubmit,
}: Props) => {
  const macId = target?.macId || '';
  const kind = target?.kind || 'unknown';
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cycles, setCycles] = useState<CycleCatalogItem[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [useCurrentCycle, setUseCurrentCycle] = useState(false);
  const [minutes, setMinutes] = useState<number>(DRYER_MINUTE_OPTIONS[0]);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !macId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCycles([]);
    setSelectedCycleId('');
    setUseCurrentCycle(false);
    setMinutes(DRYER_MINUTE_OPTIONS[0]);
    setSelectedName(null);

    axiosInstance
      .post(`/machine/${encodeURIComponent(macId)}/get-price`, {
        requested_by: requestedBy,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data as GetPriceResult;
        const catalog = filterCycleCatalog(data.cycles?.cycles);
        setCycles(catalog);
        const currentId = data.pricing?.selectedCycle?.id || '';
        setSelectedName(data.pricing?.selectedCycle?.name || null);
        if (catalog.length > 0) {
          const preferred =
            catalog.find((c) => c.id === currentId) || catalog[0];
          setSelectedCycleId(preferred.id);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(axiosErrorMessage(err, 'Failed to load cycles'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, macId, requestedBy]);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === selectedCycleId),
    [cycles, selectedCycleId]
  );

  const canSubmit =
    !busy &&
    !loading &&
    !!macId &&
    (kind === 'washer'
      ? useCurrentCycle || !!selectedCycleId
      : kind === 'dryer'
        ? !!selectedCycleId && minutes > 0
        : false);

  const handleSubmit = () => {
    if (!canSubmit || !target) return;
    if (kind === 'washer') {
      if (useCurrentCycle) {
        onSubmit({ kind, useCurrentCycle: true });
        return;
      }
      onSubmit({
        kind,
        cycleId: selectedCycleId,
        temperatureId: defaultTemperatureId(selectedCycle),
      });
      return;
    }
    onSubmit({
      kind,
      cycleId: selectedCycleId,
      minutes,
    });
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Start {target?.name || 'machine'}
        <IconButton aria-label="Close" onClick={onClose} disabled={busy} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="machine-control-modal-content">
        <p className="machine-control-meta">
          {kind === 'dryer'
            ? 'Choose heat level and minutes, then start.'
            : kind === 'washer'
              ? 'Choose a wash cycle, or start the cycle already selected on the machine.'
              : 'Machine type unknown — cannot start.'}
        </p>

        {loading && (
          <div className="machine-control-empty">
            <CircularProgress size={22} /> Loading cycles…
          </div>
        )}

        {(loadError || error) && (
          <Alert severity="error">{loadError || error}</Alert>
        )}

        {!loading && !loadError && kind === 'washer' && (
          <>
            <section className="machine-control-section">
              <h3>Cycle</h3>
              {cycles.length === 0 ? (
                <p className="machine-control-empty">No cycles returned from Alliance.</p>
              ) : (
                <div className="machine-control-options">
                  {cycles.map((c) => (
                    <Button
                      key={c.id}
                      className="machine-control-option"
                      variant={
                        !useCurrentCycle && selectedCycleId === c.id
                          ? 'contained'
                          : 'outlined'
                      }
                      size="small"
                      disabled={busy}
                      onClick={() => {
                        setUseCurrentCycle(false);
                        setSelectedCycleId(c.id);
                      }}
                    >
                      {cycleLabel(c.name, c.id)}
                    </Button>
                  ))}
                </div>
              )}
            </section>
            <section className="machine-control-section">
              <Button
                variant={useCurrentCycle ? 'contained' : 'outlined'}
                size="small"
                disabled={busy}
                onClick={() => setUseCurrentCycle(true)}
              >
                Start current cycle
                {selectedName ? ` (${selectedName})` : ''}
              </Button>
            </section>
          </>
        )}

        {!loading && !loadError && kind === 'dryer' && (
          <>
            <section className="machine-control-section">
              <h3>Heat</h3>
              {cycles.length === 0 ? (
                <p className="machine-control-empty">No heat levels returned from Alliance.</p>
              ) : (
                <div className="machine-control-options">
                  {cycles.map((c) => (
                    <Button
                      key={c.id}
                      className="machine-control-option"
                      variant={selectedCycleId === c.id ? 'contained' : 'outlined'}
                      size="small"
                      disabled={busy}
                      onClick={() => setSelectedCycleId(c.id)}
                    >
                      {cycleLabel(c.name, c.id)}
                    </Button>
                  ))}
                </div>
              )}
            </section>
            <section className="machine-control-section">
              <h3>Minutes</h3>
              <div className="machine-control-options">
                {DRYER_MINUTE_OPTIONS.map((m) => (
                  <Button
                    key={m}
                    className="machine-control-option"
                    variant={minutes === m ? 'contained' : 'outlined'}
                    size="small"
                    disabled={busy}
                    onClick={() => setMinutes(m)}
                  >
                    {m} min
                  </Button>
                ))}
              </div>
            </section>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {busy ? 'Starting…' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MachineStartModal;
